import React, { createContext, useContext, useReducer, ReactNode } from 'react'

export interface AlertData {
  id: string
  sessionId: string
  type: string
  message: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  status: 'active' | 'resolved'
  triggeredAt: string
  resolvedAt: string | null
}

interface AlertToastState {
  queue: AlertData[]
}

type AlertToastAction =
  | { type: 'ADD_ALERT'; payload: AlertData }
  | { type: 'DISMISS_TOAST' }

const initialState: AlertToastState = {
  queue: [],
}

function alertToastReducer(
  state: AlertToastState,
  action: AlertToastAction,
): AlertToastState {
  switch (action.type) {
    case 'ADD_ALERT': {
      // Avoid adding duplicates if the same alert is already in the queue
      if (state.queue.some((a) => a.id === action.payload.id)) {
        return state
      }
      return {
        ...state,
        queue: [...state.queue, action.payload],
      }
    }
    case 'DISMISS_TOAST': {
      return {
        ...state,
        queue: state.queue.slice(1),
      }
    }
    default:
      return state
  }
}

interface AlertToastContextType extends AlertToastState {
  addAlertToast: (alert: AlertData) => void
  dismissToast: () => void
}

const AlertToastContext = createContext<AlertToastContextType | undefined>(
  undefined,
)

export function AlertToastProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(alertToastReducer, initialState)

  const addAlertToast = (alert: AlertData) => {
    dispatch({ type: 'ADD_ALERT', payload: alert })
  }

  const dismissToast = () => {
    dispatch({ type: 'DISMISS_TOAST' })
  }

  return React.createElement(
    AlertToastContext.Provider,
    { value: { ...state, addAlertToast, dismissToast } },
    children,
  )
}

export function useAlertToast() {
  const context = useContext(AlertToastContext)
  if (context === undefined) {
    throw new Error('useAlertToast must be used within an AlertToastProvider')
  }
  return context
}
